import { ReactNode } from "react";
import {
  INITIAL_F2F_SECTIONS,
  NOTE_CONTENT_SECTION_KEYS,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/initial-f2f/initial-f2f-constant";
import { INITIAL_F2F_HIDDEN_SECTION_KEYS } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/initial-f2f/initial-f2f-form-options-constant";
import { NoteSections } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/initial-f2f/initial-f2f-types";
import { filterVisibleSections } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/initial-f2f/initial-f2f-utils";
import { EncounterSumaryFormTypeEnum } from "@/services";
import {
  EntrySection,
  FAMILY_HISTORY_SECTION,
  MEDICAL_HISTORY_SECTION,
  SURGICAL_HISTORY_SECTION,
} from "@/utils/subjective-entry";
import {
  checkNull,
  RenderTextWithContainerPattern,
  ReportContainer,
  ReportSection,
} from "../constant";
import AllergiesBlock from "../_shared/AllergiesBlock";
import EntryLines from "../_shared/EntryLines";
import MedicationsBlock from "../_shared/MedicationsBlock";
import PhysicalExamBlock from "../_shared/PhysicalExamBlock";
import RosBlock from "../_shared/RosBlock";
import VitalsBlock from "../_shared/VitalsBlock";
import { hasVitals } from "../_shared/section-content";
import {
  SUMMARY_EXTRA_NOTE_SECTIONS,
  SUMMARY_FREE_TEXT_SUFFIX,
  SUMMARY_SKIPPED_SECTION_KEYS,
} from "./premier-md-f2f-constant";

interface PremierMdF2fTemplateProps {
  data: any;
}

interface RecordBlock {
  has: (data: any) => boolean;
  render: (data: any, title: string) => ReactNode;
}

const historyBlock = <T,>(
  field: string,
  section: EntrySection<T>
): RecordBlock => ({
  has: (data) => checkNull(data?.[field]),
  render: (data, title) => (
    <ReportContainer label={title}>
      <EntryLines section={section} rows={data?.[field]} />
    </ReportContainer>
  ),
});

/** Sections whose rows come from the encounter snapshot's own lists rather than `noteContent`. */
const RECORD_BLOCKS: Record<string, RecordBlock> = {
  medicalHistory: historyBlock("medicalHistories", MEDICAL_HISTORY_SECTION),
  surgicalHistory: historyBlock("surgicalHistory", SURGICAL_HISTORY_SECTION),
  familyHistory: historyBlock("familyHistories", FAMILY_HISTORY_SECTION),
  allergy: {
    has: (data) => checkNull(data?.patientAllergies),
    render: (data) => <AllergiesBlock data={data} />,
  },
  medication: {
    has: (data) => checkNull(data?.patientMedications),
    render: (data) => <MedicationsBlock data={data} />,
  },
  reviewOfSystems: {
    has: (data) => checkNull(data?.rosTemplates),
    render: (data) => <RosBlock data={data} />,
  },
  vitalSigns: {
    has: hasVitals,
    render: (data) => <VitalsBlock data={data} />,
  },
  physicalExam: {
    has: (data) => checkNull(data?.physicalTemplates),
    render: (data) => <PhysicalExamBlock data={data} />,
  },
};

const getNoteKey = (sectionKey: string): string | undefined =>
  (NOTE_CONTENT_SECTION_KEYS as Record<string, string>)[sectionKey];

/**
 * PremierMdF2fTemplate — every PREMIER_MD_* note.
 *
 * Walks the same section list the note form uses, so the report keeps the form's
 * order and titles, drops the sections the note type hides, and prints a section
 * only when it has details (from `noteContent` or the snapshot's record lists).
 */
const PremierMdF2fTemplate = ({ data }: PremierMdF2fTemplateProps) => {
  const noteSections: NoteSections = data?.noteContent?.sections ?? {};
  const formType: EncounterSumaryFormTypeEnum | undefined = data?.formType;
  const hiddenKeys = [
    ...SUMMARY_SKIPPED_SECTION_KEYS,
    ...((formType && INITIAL_F2F_HIDDEN_SECTION_KEYS[formType]) || []),
  ];
  const groups = filterVisibleSections(INITIAL_F2F_SECTIONS, hiddenKeys);

  const getText = (noteKey?: string): string =>
    (noteKey && noteSections[noteKey]?.text?.trim()) || "";

  const hasContent = (sectionKey: string): boolean =>
    !!getText(getNoteKey(sectionKey)) ||
    !!RECORD_BLOCKS[sectionKey]?.has(data) ||
    !!SUMMARY_EXTRA_NOTE_SECTIONS[sectionKey]?.some(({ noteKey }) =>
      getText(noteKey)
    );

  return (
    <>
      {groups.map((group) => {
        const visible = group.sections.filter((it) => hasContent(it.key));
        if (!visible.length) return null;
        return (
          <ReportSection key={group.heading} label={group.heading}>
            {visible.map(({ key, title }) => (
              <div key={key}>
                {RECORD_BLOCKS[key]?.render(data, title)}
                <RenderTextWithContainerPattern
                  label={
                    RECORD_BLOCKS[key]
                      ? `${title}${SUMMARY_FREE_TEXT_SUFFIX}`
                      : title
                  }
                  val={getText(getNoteKey(key))}
                />
                {SUMMARY_EXTRA_NOTE_SECTIONS[key]?.map(({ noteKey, label }) => (
                  <RenderTextWithContainerPattern
                    key={noteKey}
                    label={label}
                    val={getText(noteKey)}
                  />
                ))}
              </div>
            ))}
          </ReportSection>
        );
      })}
    </>
  );
};

export default PremierMdF2fTemplate;
